import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Observable, combineLatest, distinctUntilChanged, map, switchMap } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { CmsContent, CmsTemplate, CmsViewType } from '@fcom/core-api';
import { ReplaceParams } from '@fcom/core/utils';
import { finShare } from '@fcom/rx';
import { FlightPair } from '@fcom/ond-marketing-landing-core/interfaces';

import { OndMarketingSeoService } from './ond-marketing-seo.service';
import { constructOndHeadingPath, getLocationName } from './utils';

/**
 * Populates the city-to-city CMS `structure` template with route-derived data.
 */
@Injectable()
export class OndMarketingTemplateService {
  private route = inject(ActivatedRoute);
  private ondMarketingSeoService = inject(OndMarketingSeoService);
  private languageService = inject(LanguageService);

  private flightParams$: Observable<FlightPair> = this.route.params.pipe(
    distinctUntilChanged(
      (a, b) => a.originLocationCode === b.originLocationCode && a.destinationLocationCode === b.destinationLocationCode
    ),
    switchMap((params) => this.ondMarketingSeoService.getFlightParams(params, this.languageService.langKeyValue)),
    finShare()
  );

  private heading$: Observable<string> = this.flightParams$.pipe(
    switchMap((flightParams) =>
      this.ondMarketingSeoService.getLocations(flightParams).pipe(
        switchMap((locationData) => {
          const translationData: ReplaceParams = {
            origin: getLocationName(flightParams.origin, locationData),
            destination: getLocationName(flightParams.destination, locationData),
            originLocationCode: flightParams.origin,
            destinationLocationCode: flightParams.destination,
          };
          return this.languageService.translate<string>(
            constructOndHeadingPath('bookFlights', flightParams),
            translationData
          );
        })
      )
    ),
    distinctUntilChanged(),
    finShare()
  );

  populateCityToCityTemplate(template$: Observable<CmsTemplate>): Observable<CmsTemplate> {
    return combineLatest([template$, this.flightParams$, this.heading$]).pipe(
      map(([template, flightParams, heading]) => ({
        ...template,
        main: this.patchNodes(template.main, flightParams, heading),
      }))
    );
  }

  private patchNodes(nodes: CmsContent[], flightParams: FlightPair, heading: string): CmsContent[] {
    return (nodes ?? []).map((node) => this.patchNode(node, flightParams, heading));
  }

  private patchNode(node: CmsContent, flightParams: FlightPair, heading: string): CmsContent {
    let patched: CmsContent = node;

    switch (node.viewType) {
      case CmsViewType.DECORATED_HEADING:
        patched = { ...node, teaserTitle: heading };
        break;
      case CmsViewType.FULL_WIDTH_BOOKING_WIDGET:
        patched = {
          ...node,
          origin: { defaultLocation: { locationCode: flightParams.origin } },
          destination: { defaultLocation: { locationCode: flightParams.destination } },
        } as CmsContent;
        break;
    }

    if (Array.isArray(patched.items)) {
      patched = { ...patched, items: this.patchNodes(patched.items, flightParams, heading) };
    }

    return patched;
  }
}
